import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as React from 'react';

import {
  SIZE_SCALE,
  COLOR_SCALE,
  RADIUS_SCALE,
  TYPE_SCALE,
  FONT_WEIGHTS,
  TEXT,
  TRANSITION,
  INTERACTIVE_STATES,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
  animated?: boolean;
  label?: string;
}

const Progress = React.forwardRef<React.ElementRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      showValue = false,
      animated = true,
      value,
      label,
      ...props
    },
    ref
  ) => {
    const variants = {
      default: COLOR_SCALE.primary,
      success: COLOR_SCALE.success,
      warning: COLOR_SCALE.warning,
      error: COLOR_SCALE.error,
      info: COLOR_SCALE.info,
    };

    const sizes = {
      sm: SIZE_SCALE['1'],
      md: SIZE_SCALE['2'],
      lg: SIZE_SCALE['3'],
    };

    const safeValue = value ?? 0;

    return (
      <div className="flex flex-col gap-2">
        {label && (
          <div className="flex justify-between items-center">
            <span className={`${TYPE_SCALE.sm} ${FONT_WEIGHTS.medium} ${COLOR_SCALE.foreground}`}>
              {label}
            </span>
            {showValue && value !== undefined && (
              <span className={`${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}>
                {Math.round(safeValue)}%
              </span>
            )}
          </div>
        )}
        <ProgressPrimitive.Root
          ref={ref}
          className={cn(
            `relative ${sizes[size]} w-full overflow-hidden ${RADIUS_SCALE.full} bg-muted`,
            className
          )}
          {...props}
        >
          <ProgressPrimitive.Indicator
            className={cn(
              `h-full ${RADIUS_SCALE.full} transition-all duration-500 ease-out`,

              variants[variant],
              animated && safeValue > 0 && 'animate-pulse'
            )}
            style={{
              transform: `translateX(-${100 - safeValue}%)`,
              transition: animated ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            }}
          />
        </ProgressPrimitive.Root>
      </div>
    );
  }
);

Progress.displayName = ProgressPrimitive.Root.displayName;

// Step-based progress indicator for multi-step processes
interface StepProgressProps {
  currentStep: number;
  totalSteps: number;
  steps?: string[];
  variant?: 'dots' | 'bars' | 'numbers';
  className?: string;
}

const StepProgress = ({
  currentStep,
  totalSteps,
  steps = [],
  variant = 'dots',
  className,
}: StepProgressProps) => {
  const renderDots = () => (
    <div className={cn('flex items-center justify-center gap-2', className)}>
      {Array.from({ length: totalSteps }).map((_, index) => {
        const stepNumber = index + 1;
        const isCompleted = currentStep > stepNumber;
        const isCurrent = currentStep === stepNumber;
        const isUpcoming = currentStep < stepNumber;

        return (
          <div key={index} className="flex flex-col items-center gap-1">
            <div
              className={cn('w-3 h-3 rounded-full transition-all duration-300', {
                'bg-primary scale-125 shadow-md': isCompleted,
                'bg-primary animate-pulse scale-110 shadow-sm': isCurrent,
                'bg-muted-foreground/30': isUpcoming,
              })}
            />
            {steps[index] && (
              <span
                className={cn('text-xs transition-colors duration-300', {
                  'text-primary font-medium': isCompleted || isCurrent,
                  'text-muted-foreground': isUpcoming,
                })}
              >
                {steps[index]}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderBars = () => (
    <div className={cn('flex items-center gap-1', className)}>
      {Array.from({ length: totalSteps }).map((_, index) => {
        const stepNumber = index + 1;
        const isCompleted = currentStep > stepNumber;
        const isCurrent = currentStep === stepNumber;

        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-1">
            <div
              className={cn(
                `${SIZE_SCALE.full} ${SIZE_SCALE['2']} ${RADIUS_SCALE.full} ${TRANSITION.all} ${INTERACTIVE_STATES.duration.slower}`,
                {
                  'bg-primary': isCompleted,
                  'bg-primary animate-pulse': isCurrent,
                  'bg-muted-foreground/30': !isCompleted && !isCurrent,
                }
              )}
            />
            {}
            {steps[index] && (
              <span
                className={cn('text-xs text-center transition-colors duration-300', {
                  'text-primary font-medium': isCompleted || isCurrent,
                  'text-muted-foreground': !isCompleted && !isCurrent,
                })}
              >
                {}
                {steps[index]}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderNumbers = () => (
    <div className={cn('flex items-center justify-center gap-4', className)}>
      {Array.from({ length: totalSteps }).map((_, index) => {
        const stepNumber = index + 1;
        const isCompleted = currentStep > stepNumber;
        const isCurrent = currentStep === stepNumber;
        const isUpcoming = currentStep < stepNumber;

        return (
          <div key={index} className="flex flex-col items-center gap-1">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 border-2',
                {
                  'bg-primary text-primary-foreground border-primary scale-110 shadow-md':
                    isCompleted,
                  'bg-primary text-primary-foreground border-primary animate-pulse scale-105 shadow-sm':
                    isCurrent,
                  'bg-background text-muted-foreground border-muted-foreground/30': isUpcoming,
                }
              )}
            >
              {isCompleted ? '✓' : stepNumber}
            </div>
            {steps[index] && (
              <span
                className={cn(
                  `${TYPE_SCALE.xs} ${TEXT.center} ${SIZE_SCALE.maxW80} ${TRANSITION.colors} ${INTERACTIVE_STATES.duration.slow}`,
                  {
                    'text-primary font-medium': isCompleted || isCurrent,
                    'text-muted-foreground': isUpcoming,
                  }
                )}
              >
                {steps[index]}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );

  switch (variant) {
    case 'bars':
      return renderBars();
    case 'numbers':
      return renderNumbers();
    case 'dots':
    default:
      return renderDots();
  }
};

export { Progress, StepProgress };
